import React, { FC, useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Card, Container } from 'reactstrap';

import { isAfter, isValid, parseISO, startOfToday, startOfTomorrow } from 'date-fns';
import { toNumber } from 'lodash-es';
import ArrowLeftIcon from 'material-react-icons/KeyboardArrowLeft';

import Accommodation from '../../components/Accommodation/Accommodation';
import Button from '../../components/Button/Button';
import PageSpinner from '../../components/PageSpinner';
import Helmet from '../../components/common/Helmet';
import { formatISODate } from '../../helpers/formatISODate';
import useTranslate from '../../hooks/useTranslate';
import { useAccommodation, useAccommodationItems } from '../../services/apis/accommodation/api';
import useGolfCourses from '../../services/apis/golfCourses/api';
import getGolfClubInfo from '../../services/apis/golfCourses/getGolfClubInfo';

const BookingAccommodationPage: FC = () => {
  const params = useParams<'accommodationId'>();
  let accommodationId = toNumber(params.accommodationId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const onteeClubId = toNumber(searchParams.get('ontee-club-id'));
  const { translate } = useTranslate();
  const [[checkinDate, checkoutDate], setDates] = useState(() => {
    const checkinDateParam = parseISO(searchParams.get('checkinDate') ?? '');
    const checkoutDateParam = parseISO(searchParams.get('checkoutDate') ?? '');
    const today = startOfToday();
    const tomorrow = startOfTomorrow();

    return [
      formatISODate(
        isValid(checkinDateParam) && isAfter(checkinDateParam, today) ? checkinDateParam : today
      ),
      formatISODate(
        isValid(checkoutDateParam) && isAfter(checkoutDateParam, tomorrow)
          ? checkoutDateParam
          : tomorrow
      ),
    ];
  });

  const { activeLanguage } = useTranslate();

  useEffect(() => {
    if (accommodationId && checkinDate && checkoutDate) {
      navigate({
        search: new URLSearchParams({
          checkinDate,
          checkoutDate,
        }).toString(),
      });
    }
  }, [accommodationId, checkinDate, checkoutDate, navigate]);

  // todo load ontee.accommodations.available from src/components/AccommodationsBooking.js:69
  const accommodationQuery = useAccommodation(accommodationId);

  const golfCoursesQuery = useGolfCourses();
  const golfCourse = golfCoursesQuery.data?.find(
    course => getGolfClubInfo(course)?.accommodationId === accommodationId
  );
  const golfClubInfo = golfCourse ? getGolfClubInfo(golfCourse) : null;

  const accommodationItemsQuery = useAccommodationItems(accommodationId, checkinDate, checkoutDate);

  // todo render data like in the modal from src/components/AccommodationsBooking.js:138

  if (accommodationQuery.isLoading || golfCoursesQuery.isLoading) {
    return <PageSpinner />;
  }

  if (!accommodationId && onteeClubId && golfCoursesQuery.data) {
    const golfCourseByOnTeeClubId = golfCoursesQuery.data.find(
      course => course.golfClub.id === onteeClubId
    );
    const accommodationIdToRedirect =
      golfCourseByOnTeeClubId && getGolfClubInfo(golfCourseByOnTeeClubId)?.accommodationId;

    if (accommodationIdToRedirect) {
      return (
        <Navigate
          to={`/${activeLanguage.code}/booking/accommodation/${accommodationIdToRedirect}/`}
          replace
        />
      );
    }
  }

  if (!accommodationQuery.data || !golfClubInfo) {
    // TODO where to redirect to? /en/booking page is always empty without selected golf course
    // return <Navigate to={`/${activeLanguage.code}/booking`} replace />;
    return <Navigate to={`/${activeLanguage.code}/`} replace />;
  }

  let backUrl = `/${activeLanguage.code}/`;

  if (golfCourse) {
    backUrl = `/${activeLanguage.code}/booking/${golfCourse.golfClub.name}/${golfCourse.golfCourse.name}/?acc=1`;
  }

  return (
    <>
      <Helmet
        titleTranslationKey="accommodation.helmetTitle"
        descriptionTranslationKey="accommodation.helmetDescription"
      />

      <Container className="section pt-1 mt-3">
        <Button tertiary tag={Link} to={backUrl} className="mb-4">
          <ArrowLeftIcon size={30} />
          {String(translate('default.goBackCTA'))}
        </Button>
        <Card className="p-3">
          <Accommodation
            accommodation={accommodationQuery.data}
            accommodationItems={accommodationItemsQuery.data}
            loading={accommodationItemsQuery.isLoading || accommodationItemsQuery.isPreviousData}
            checkinDate={checkinDate}
            checkoutDate={checkoutDate}
            onDateChange={setDates}
          />
        </Card>
        {backUrl && (
          <Button tertiary tag={Link} to={backUrl} className="my-4">
            <ArrowLeftIcon size={30} />
            {String(translate('default.goBackCTA'))}
          </Button>
        )}
      </Container>
    </>
  );
};

export default BookingAccommodationPage;
